import React, { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Flame, Cake, RefreshCw, Clock, Star, FileText, ClipboardList, PauseCircle } from 'lucide-react';
import type { IntlLike } from '../clientPresentation';
import type { ClientStatKey, ClientStats } from '../clientListView';
import type { RecruitStatKey, RecruitStats } from '../recruitListView';
import { RECRUIT_FLIP_MS, RECRUIT_STAGGER_MS } from '../recruitModeTransition';

// The 4 stat tiles (handoff PART 6c). Per the colour-budget pass (PART 6p) the
// icons are all one indigo accent - differentiated by icon SHAPE, not hue - so
// the row reads as one system rather than four competing colour languages.
//
// `countDelay` is each tile's slide-up delay from styles.scss (0.30/0.44/0.58/
// 0.72s) plus ~0.55s - the ease-out curve has the tile visually settled by
// then - so the figure starts counting the moment its own card lands. The two
// files have to be kept in step by hand; a figure counting before its card
// arrives reads as a glitch, not as choreography.
const TILES: { key: ClientStatKey; icon: typeof Flame; labelKey: string; variant: string; countDelay: number }[] = [
  {
    key: 'highPotential',
    icon: Flame,
    labelKey: 'clientv2-stat-high-potential',
    variant: 'is-high-potential',
    countDelay: 850
  },
  { key: 'birthdayThisMonth', icon: Cake, labelKey: 'clientv2-stat-birthday', variant: 'is-birthday', countDelay: 990 },
  {
    key: 'policyDueSoon',
    icon: RefreshCw,
    labelKey: 'clientv2-stat-policy-due',
    variant: 'is-policy-due',
    countDelay: 1130
  },
  {
    key: 'longTimeNoSee',
    icon: Clock,
    labelKey: 'clientv2-stat-long-no-see',
    variant: 'is-long-no-see',
    countDelay: 1270
  }
];

/**
 * 招募模式's four, in the same three-plus-one shape the business row has: one
 * says where the opportunity is, three say what has been left undone.
 *
 * 未開始 and 待評估 are mutually exclusive by construction (recruitListView):
 * the first is 「nobody has begun」, the second 「begun, but we still do not know
 * them」. Defined any other way the two tiles would open largely the same list,
 * and two figures describing one group of people is worse than one figure.
 */
const RECRUIT_TILES: {
  key: RecruitStatKey;
  icon: typeof Flame;
  labelKey: string;
  variant: string;
  countDelay: number;
}[] = [
  {
    key: 'priority',
    icon: Star,
    labelKey: 'clientv2-recruit-stat-priority',
    variant: 'is-rc-priority',
    countDelay: 850
  },
  {
    key: 'notStarted',
    icon: FileText,
    labelKey: 'clientv2-recruit-stat-not-started',
    variant: 'is-rc-not-started',
    countDelay: 990
  },
  {
    key: 'unrated',
    icon: ClipboardList,
    labelKey: 'clientv2-recruit-stat-unrated',
    variant: 'is-rc-unrated',
    countDelay: 1130
  },
  {
    key: 'stalled',
    icon: PauseCircle,
    labelKey: 'clientv2-recruit-stat-stalled',
    variant: 'is-rc-stalled',
    countDelay: 1270
  }
];

export type StatTilesProps = {
  stats: ClientStats;
  /** The tile currently narrowing the list, if any. */
  activeStat?: ClientStatKey | null;
  /**
   * Filters the list to the clients behind a tile; called again with the same
   * key to clear it, so the tile is its own on/off control. Omitted where there
   * is no list to narrow (tests, story states), and the tiles then render as the
   * plain read-outs they were — a button that filtered nothing would be a lie.
   */
  onStat?: (stat: ClientStatKey) => void;
  /**
   * 招募模式. The row swaps its four tiles for the recruitment ones; everything
   * else about it — the slide-up, the staggered count-up, tapping a tile to
   * narrow the list — is the same control doing the same job, which is why this
   * is a variant of StatTiles rather than a second component.
   */
  recruitMode?: boolean;
  recruitStats?: RecruitStats;
  activeRecruitStat?: RecruitStatKey | null;
  onRecruitStat?: (stat: RecruitStatKey) => void;
  /**
   * The beat in the mode transition where the row turns in behind the colour
   * wipe. The tiles turn IN only, never out: the old four are hidden under the
   * overlay at that moment anyway, and a full flip would have to show the old
   * face to something — either before the wipe covers it, or after it clears.
   */
  entering?: boolean;
  intl: IntlLike;
};

// Ease-out-cubic count-up for the tile figures. The FIRST render shows the
// value as-is (tests, and any already-loaded data, see the real number); the
// animation only runs when the value CHANGES afterwards - e.g. stats computed
// from clients that arrive async after mount - which is exactly the moment
// the motion carries information. Reduced-motion users and environments
// without rAF jump straight to the target.
//
// `firstDelay` postpones only the FIRST count after mount: the stat tiles use
// it to start each figure as its own slide-up lands (the per-tile delays in
// styles.scss), so the card and its number read as one motion instead of two
// independent ones. Every LATER change (a client marked high-potential
// mid-session) counts at once - a live update must never wait on choreography.
// Exported: TodayQueue reuses it for the progress percentage, so the meter
// counts with the same easing as the tiles.
export const useCountUp = (target: number, duration = 1500, firstDelay = 0, startFrom?: number): number => {
  // `startFrom` makes the FIRST render show that number and count up from it,
  // for figures that mount as part of a motion (the mode flip) rather than
  // with data already on screen.
  const [value, setValue] = useState(startFrom ?? target);
  const previous = useRef(startFrom ?? target);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const from = previous.current;
    previous.current = target;
    const reduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (from === target || reduced || typeof requestAnimationFrame !== 'function') {
      setValue(target);
      return;
    }
    const wait = hasAnimated.current ? 0 : firstDelay;
    hasAnimated.current = true;
    let raf = 0;
    let timer = 0;
    const begin = () => {
      const start = performance.now();
      const tick = (now: number) => {
        const progress = Math.min(1, (now - start) / duration);
        setValue(Math.round(from + (target - from) * (1 - Math.pow(1 - progress, 3))));
        if (progress < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    if (wait > 0) timer = window.setTimeout(begin, wait);
    else begin();
    return () => {
      window.clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [target, duration, firstDelay]);

  return value;
};

// One hook call per tile, so the figure is its own component rather than four
// hook calls inside a .map (which would break the rules of hooks).
const CountUpValue = ({ value, delay, fromZero }: { value: number; delay: number; fromZero?: boolean }) => (
  <>{useCountUp(value, 1500, delay, fromZero ? 0 : undefined)}</>
);

const StatTiles = (props: StatTilesProps) => {
  const { stats, activeStat, onStat, intl } = props;
  const { recruitMode = false, recruitStats, activeRecruitStat, onRecruitStat, entering = false } = props;
  const unit = intl.formatMessage({ id: 'clientv2-stat-unit' });

  // Once the row has flipped to the other mode, the page-load slide-up must not
  // play again: the tiles remount with new keys, and when `is-entering` drops
  // off the base rule would replay it right after the flip. From then on the
  // flip is the only entrance, and each new figure counts up from 0 as its
  // card turns in (a remounted figure would otherwise just show its total).
  const mountedMode = useRef(recruitMode);
  const settled = recruitMode !== mountedMode.current || entering;
  const settledRef = useRef(false);
  if (settled) settledRef.current = true;
  const flipped = settledRef.current;

  // One row, two vocabularies —— 而兩排都有自己的四個色。招募那四張原本不上色,
  // 結果在一片琥珀色的頁面上,整排變成唯一一塊灰:形狀分得出是哪一張,卻分不出
  // 哪一張要緊。色相照業務那排的同一套語言排(暖色=機會、紅=落後),只是換成
  // 招募自己的調子。
  const tiles = recruitMode
    ? RECRUIT_TILES.map(tile => ({
        ...tile,
        value: recruitStats?.[tile.key] ?? 0,
        active: activeRecruitStat === tile.key,
        press: () => onRecruitStat?.(tile.key)
      }))
    : TILES.map(tile => ({
        ...tile,
        value: stats[tile.key],
        active: activeStat === tile.key,
        press: () => onStat?.(tile.key)
      }));
  const pressable = recruitMode ? !!onRecruitStat : !!onStat;

  return (
    <div
      className={`clientv2-stats${entering ? ' is-entering' : ''}${flipped ? ' is-settled' : ''}`}
      data-testid="stat-tiles"
      // The turn-in timings come from recruitModeTransition, the same constant
      // the wipe is cut from. They were literals in the stylesheet, so changing
      // RECRUIT_TRANSITION_SPEED — which that module says is all it takes —
      // left the overlay clearing while the last tiles were still turning.
      style={
        {
          '--cv2-flip-ms': `${RECRUIT_FLIP_MS}ms`,
          '--cv2-stagger-ms': `${RECRUIT_STAGGER_MS}ms`
        } as React.CSSProperties
      }
    >
      {tiles.map((tile, index) => {
        const Icon = tile.icon;
        const active = tile.active;
        // The label and figure already read as 「高潛力客戶 11 名」, which is the
        // accessible name a filter control wants; aria-pressed carries the rest.
        const content = (
          <>
            <span className="clientv2-stat-icon">
              <Icon size={19} aria-hidden="true" />
            </span>
            <div className="clientv2-stat-body">
              <div className="clientv2-stat-label">{intl.formatMessage({ id: tile.labelKey })}</div>
              <div className="clientv2-stat-value" data-testid={`stat-${tile.key}`}>
                <CountUpValue
                  value={tile.value}
                  // Starts as the card is half-turned, staggered like the flip.
                  delay={flipped ? RECRUIT_STAGGER_MS * index + RECRUIT_FLIP_MS / 2 : tile.countDelay}
                  fromZero={flipped}
                />
                <span>{unit}</span>
              </div>
            </div>
          </>
        );
        const className = `clientv2-stat ${tile.variant}${active ? ' is-active' : ''}`.replace(/  +/g, ' ');

        return pressable ? (
          <button
            key={tile.key}
            type="button"
            className={className}
            data-testid="stat-tile"
            aria-pressed={active}
            onClick={tile.press}
          >
            {content}
          </button>
        ) : (
          <div key={tile.key} className={className} data-testid="stat-tile">
            {content}
          </div>
        );
      })}
    </div>
  );
};

export default injectIntl(StatTiles);
